import {
  Component,
  computed,
  inject,
  Input,
  input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FmDocument,
  IncidentClient,
  IncidentViewModel,
  ResultExtensionWrapperOfTaskViewModelAndIEnumerableOfIncidentFieldMapping,
  TaskClient,
} from '@core/api';
import { BreadcrumbService } from '@core/services/breadcrumb-service/BreadcrumbService';
import { SessionStorageService } from '@core/services/session-storage-service/session-storage.service';
import { SpeedikonService } from '@core/services/speedikon/speedikon.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const documentsEntitiesKey = 'documentsEntities';
const ticketNumberKey = 'ticketNumber';
const incidentIdKey = 'incidentId';
const taskIdKey = 'taskId';

@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DocumentsComponent implements OnInit {
  @Input() hasDocumentTypeSelection = true;

  @Input() hasDocumentVisibilitySelection = true;

  @Input() defaultDocumentTypeValue = null;

  @Input() defaultDocumentVisibilityValue = null;

  readonly speedikonService = inject(SpeedikonService);
  incident$: Observable<IncidentViewModel | undefined>;

  task$: Observable<
    | ResultExtensionWrapperOfTaskViewModelAndIEnumerableOfIncidentFieldMapping
    | undefined
  >;

  buildingDocument = input<FmDocument[]>();

  loadingError$ = new Subject<boolean>();

  showIncident: boolean;

  showTask: boolean;

  showBuilding = computed(() => {
    const documents = this.buildingDocument();
    return documents !== undefined && documents !== null;
  });

  SpeedikonArchObjId = computed(() =>
    this.speedikonService.savedArchObjIdSignal()
  );

  showBackButton: boolean;

  incidentIdFromState: string;

  viewTitle = this.translateService.instant('documents.title');

  navigation = this.router.getCurrentNavigation();

  originRoute: string;

  constructor(
    private sessionStorageService: SessionStorageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private incidentClient: IncidentClient,
    private taskClient: TaskClient,
    private breadcrumbService: BreadcrumbService,
    private translateService: TranslateService
  ) {
    if (this.navigation?.extras?.state) {
      this.originRoute = this.navigation.extras.state?.origin;
    }

    this.incidentIdFromState =
      this.router.getCurrentNavigation()?.extras?.state?.incidentId;
    if (this.incidentIdFromState) {
      this.showBackButton = true;
    }
  }

  setBreadcrumbs() {
    let label = this.translateService.instant(
      'menu.tickets_tasks.component.tickets_view'
    );
    let url = '/tickets';

    if (this.originRoute === 'ticketListDtag') {
      label = this.translateService.instant(
        'menu.tickets_tasks.component.tickets'
      );
      url = '/tickets_dtag';
    }

    if (this.buildingDocument()) {
      return;
    }

    this.breadcrumbService.setBreadcrumbs([
      {
        label,
        onClick: () => {
          this.router.navigateByUrl(url);
        },
      },
      {
        label: this.viewTitle,
      },
    ]);
  }

  ngOnInit() {
    this.setBreadcrumbs();
    this.loadData();
  }

  loadData() {
    if (this.showBuilding()) {
      return;
    }

    const ticketNumber =
      this.activatedRoute.snapshot.paramMap.get(ticketNumberKey);
    if (ticketNumber) {
      this.initViaTicketNumber(ticketNumber);
    }

    const incidentId =
      this.activatedRoute.snapshot.paramMap.get(incidentIdKey) ||
      this.incidentIdFromState;
    if (incidentId) {
      this.initViaIncidentId(incidentId);
    }

    const taskId = this.activatedRoute.snapshot.paramMap.get(taskIdKey);
    if (taskId) {
      this.initViaTaskId(taskId);
    }

    if (!ticketNumber && !incidentId && !taskId) {
      const documentsEntities =
        this.sessionStorageService.get(documentsEntitiesKey);
      const ticketNumberFallback = documentsEntities?.ticketNumber;
      if (ticketNumberFallback) {
        this.initViaTicketNumber(ticketNumberFallback);
      }

      const incidentIdFallback = documentsEntities?.incidentId;
      if (incidentIdFallback) {
        this.initViaIncidentId(incidentIdFallback);
      }

      const taskIdFallback = documentsEntities?.taskId;
      if (taskIdFallback) {
        this.initViaTaskId(taskIdFallback);
      }
    }
  }

  initViaTicketNumber(ticketNumber: string) {
    this.showIncident = true;
    this.incident$ = this.getByTicketNumber(ticketNumber);
    this.clearLocalStorageIds();
    this.sessionStorageService.set(documentsEntitiesKey, { ticketNumber });
  }

  initViaIncidentId(incidentId: string) {
    this.showIncident = true;
    this.incident$ = this.getByIncidentId(incidentId);
    this.clearLocalStorageIds();
    this.sessionStorageService.set(documentsEntitiesKey, { incidentId });
  }

  initViaTaskId(taskId: string) {
    this.showTask = true;
    this.task$ = this.getByTaskId(taskId);
    this.clearLocalStorageIds();
    this.sessionStorageService.set(documentsEntitiesKey, { taskId });
  }

  clearLocalStorageIds() {
    this.sessionStorageService.remove(documentsEntitiesKey);
  }

  getByTicketNumber(ticketNumber: string) {
    return this.incidentClient
      .getIncidentByTicketIdAuthenticated(ticketNumber)
      .pipe(
        map((result: IncidentViewModel) => {
          return result;
        }),
        catchError(() => {
          this.loadingError$.next(true);
          return of(undefined);
        })
      );
  }

  getByIncidentId(id: string) {
    return this.incidentClient.getIncidentAuthenticated(id).pipe(
      map((result: IncidentViewModel) => {
        return result;
      }),
      catchError(() => {
        this.loadingError$.next(true);
        return of(undefined);
      })
    );
  }

  getByTaskId(id: string) {
    return this.taskClient.getTask(id, false).pipe(
      map(
        (
          result: ResultExtensionWrapperOfTaskViewModelAndIEnumerableOfIncidentFieldMapping
        ) => {
          return result;
        }
      ),
      catchError(() => {
        this.loadingError$.next(true);
        return of(undefined);
      })
    );
  }
}
